import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";

/**
 * A question from the bank, shown inside this page.
 *
 * It is an iframe rather than a copy of the renderer. The bank renders
 * questions with the same component the tutor approved them in; a second
 * renderer here would drift from that, and a student must see what was
 * approved. The bank serves /embed/<code> with no chrome for exactly this.
 *
 * The frame tells us its height as it changes - KaTeX typesets after first
 * paint and diagrams arrive later still, so a height measured once on load is
 * always too short and the question gets clipped.
 */
const BANK = process.env.REACT_APP_QBANK_URL || "https://qbank.geniuseducation.co.uk";

export default function EmbeddedQuestion({ code }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(240);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onMessage = (e) => {
      if (!e.data || e.data.type !== "qbank:height" || e.data.code !== code) return;
      const h = Number(e.data.height);
      if (Number.isFinite(h) && h > 40) { setHeight(h); setLoaded(true); }
    };
    window.addEventListener("message", onMessage);
    // If nothing arrives the frame is blocked or the bank is down. Say so and
    // offer the link, rather than leaving an empty box that looks like a
    // question with no content.
    const t = setTimeout(() => setFailed((f) => (loaded ? f : true)), 6000);
    return () => { window.removeEventListener("message", onMessage); clearTimeout(t); };
  }, [code, loaded]);

  return (
    <div className="relative border border-slate-200 rounded-lg overflow-hidden bg-white">
      {!loaded && !failed && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-slate-400 bg-white">
          <Loader2 size={15} className="animate-spin" /> Loading the question…
        </div>
      )}
      {failed && !loaded && (
        <div className="px-4 py-6 text-sm text-slate-600">
          <p>The question would not load here.</p>
          <a href={`${BANK}/q/${code}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 underline mt-1">
            Open it in a new tab <ExternalLink size={12} />
          </a>
        </div>
      )}
      <iframe
        ref={ref}
        title={`Question ${code}`}
        src={`${BANK}/embed/${code}`}
        style={{ width: "100%", height, border: 0, display: failed && !loaded ? "none" : "block" }}
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}
